import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, FlaskConical, Plus, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { EvalSuiteSummary } from "@/api/generated/EvalSuiteSummary"
import { useDeleteSuite, useEvalSuites, useRegressionAlerts } from "@/api/queries/evals"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { formatPassRate, passRateTone } from "@/features/evals/format"
import { RegressionAlerts } from "@/features/evals/regression-alerts"
import { SuiteDialog } from "@/features/evals/suite-dialog"
import { formatNumber } from "@/lib/format"

export const Route = createFileRoute("/_app/studio/evals/")({
  staticData: { titleKey: "nav.items.evals" },
  component: EvalsPage,
})

function EvalsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const suites = useEvalSuites()
  const alerts = useRegressionAlerts()
  const remove = useDeleteSuite()
  const confirm = useConfirm()
  const [creating, setCreating] = useState(false)
  const open = (id: number) =>
    void navigate({ to: "/studio/evals/$suiteId", params: { suiteId: String(id) } })

  const columns = useMemo(
    () =>
      createColumns<EvalSuiteSummary>((c) => [
        c.accessor("name", {
          header: t("studio.evals.columns.suite"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="max-w-md min-w-44">
              <div className="truncate font-medium">{row.original.name}</div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.description || t("studio.evals.noDescription")}
              </div>
            </div>
          ),
        }),
        c.accessor("agent_name", {
          header: t("studio.evals.columns.agent"),
          cell: ({ getValue }) => <span className="text-[0.8125rem]">{getValue()}</span>,
        }),
        c.accessor("case_count", {
          header: t("studio.evals.columns.cases"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("last_run_pass_rate", {
          header: t("studio.evals.columns.passRate"),
          meta: { align: "end" },
          cell: ({ getValue }) =>
            getValue() == null ? (
              <span className="text-muted-foreground text-xs">{t("studio.evals.notRunYet")}</span>
            ) : (
              <StatusBadge tone={passRateTone(getValue())} className="tabular-nums">
                {formatPassRate(getValue())}
              </StatusBadge>
            ),
        }),
        c.accessor("last_run_at", {
          header: t("studio.evals.columns.lastRun"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.evals.title")}
        description={t("studio.evals.description")}
        icon={FlaskConical}
        actions={
          <Button onClick={() => setCreating(true)} data-testid="new-suite">
            <Plus />
            {t("studio.evals.newSuite")}
          </Button>
        }
      />
      <div className="space-y-6">
        <RegressionAlerts alerts={alerts.data ?? []} />
        <DataTable
          label={t("studio.evals.title")}
          data={suites.data}
          columns={columns}
          getRowId={(s) => String(s.id)}
          loading={suites.isPending}
          error={suites.error}
          onRetry={() => void suites.refetch()}
          searchable
          searchPlaceholder={t("studio.evals.search")}
          initialSorting={[{ id: "name", desc: false }]}
          onRowClick={(s) => open(s.id)}
          rowActions={(s) => (
            <>
              <DropdownMenuItem onClick={() => open(s.id)}>
                <ArrowRight />
                {t("studio.evals.openSuite")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() =>
                  void confirm({
                    title: t("studio.evals.deleteSuiteTitle", { name: s.name }),
                    description: t("studio.evals.deleteSuiteDescription"),
                    confirmLabel: t("studio.evals.deleteSuite"),
                    destructive: true,
                    confirmText: s.name,
                    onConfirm: () => remove.mutateAsync(s),
                  })
                }
              >
                <Trash2 />
                {t("studio.evals.deleteSuite")}
              </DropdownMenuItem>
            </>
          )}
          emptyState={
            <EmptyState
              variant="plain"
              icon={FlaskConical}
              title={t("studio.evals.empty.title")}
              description={t("studio.evals.empty.description")}
              action={
                <Button size="sm" onClick={() => setCreating(true)}>
                  <Plus />
                  {t("studio.evals.newSuite")}
                </Button>
              }
            />
          }
        />
      </div>
      <SuiteDialog open={creating} onOpenChange={setCreating} onCreated={(s) => open(s.id)} />
    </PageContainer>
  )
}
